<template>
  <ContentCard :loading="loading" :progress="uploadProgress">
    <template #header>
      <ReviewsProductTabs />
    </template>

    <template #subheader>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsProductView }">
        {{ t('Reviews.product.edit.detailsTitle', { productName: product.title }) }}
        <template #actions>
          <RouterLink
            class="btn-secondary-color-s mr-2"
            data-testid="cancel-button"
            :to="{ name: ReviewsRouteName.ReviewsProductViewDetails, params: { productId: product.id } }"
            @click="track('profile_update_cancel', { surface: 'editor', entity_type: 'tool', entity_id: product.id })"
          >
            {{ t('Reviews.product.action.cancel') }}
          </RouterLink>
          <button class="btn-primary-color-s" data-testid="save-button" @click="onSubmit">{{ t('Reviews.product.action.save') }}</button>
        </template>
      </ContentCardSubheader>
    </template>

    <FencingBanner
      v-if="fencedFeatureList.length > 0"
      :headline="t('Reviews.product.view.media.banner.detailsFencingHeadline')"
      :feature-list="fencedFeatureList"
      :cta-label="t('Reviews.product.view.media.banner.detailsFencingCtaLabel')"
      :cta-link="t('Reviews.product.view.media.banner.detailsFencingCtaLink')"
      :banner-image="detailsFencingImageUrl"
      placement="banner"
      entity-type="tool"
      :entity-id="product.id"
    />
    <div class="pt-6">
      <LabelValue :label="t('Reviews.product.view.category.label').toString()" show-icon>
        <template #hint>
          <span v-html="t('Reviews.product.view.category.hint')" />
        </template>
        <div class="w-1/2">
          <TextInput
            data-testid="product-category-input"
            disabled
            :label="t('Reviews.product.view.category.label').toString()"
            :model-value="productCategories.join(', ')"
          />
        </div>
      </LabelValue>
      <div class="grid grid-cols-4">
        <LabelValue :label="t('Reviews.product.view.logo.label').toString()">
          <MediaUpload
            data-testid="product-logo-upload"
            :accept="LOGO_MIME_TYPES.join(', ')"
            :preview-asset="product.logo || undefined"
            :hint="t('Reviews.product.view.logo.hint').toString()"
            :short-hint="shortLogoHint"
            :error="getValidationErrors(v$.logo)"
            @upload="handleLogoUpload"
          >
            <template #preview="previewProps">
              <ProductLogo :logo-url="previewProps.url" size="large" />
            </template>
          </MediaUpload>
        </LabelValue>
      </div>

      <FeatureFencingWrapper
        :fenced="heroImageFenced"
        :label="t('Reviews.product.view.detailsEdit.heroFencingLabel')"
        :help-tooltip="t('Reviews.product.view.detailsEdit.heroFencingTooltip')"
        :help-link="t('Reviews.product.view.detailsEdit.heroFencingHelpLink')"
        :upgrade-cta="t('Reviews.product.view.detailsEdit.heroFencingUpgradeCta')"
        :upgrade-cta-link="t('Reviews.product.view.detailsEdit.heroFencingUpgradeCtaLink')"
        placement="hero"
        entity-type="tool"
        :entity-id="product.id"
      >
        <LabelValue
          :label="t('Reviews.product.view.hero.label').toString()"
          :class="{ 'text-disabled': heroImageFenced }"
          :help-tooltip="!heroImageFenced ? t('Reviews.product.view.detailsEdit.heroFencingTooltip') : undefined"
          :help-link="!heroImageFenced ? t('Reviews.product.view.detailsEdit.heroFencingHelpLink') : undefined"
        >
          <MediaUpload
            data-testid="product-hero-image-upload"
            :accept="HERO_MIME_TYPES.join(', ')"
            aspect-ratio="16:9"
            :disabled="heroImageFenced"
            max-width="648px"
            :preview-asset="heroImageFenced ? undefined : product.hero || undefined"
            :hint="heroHint"
            :short-hint="shortHeroHint"
            :error="getValidationErrors(v$.hero)"
            @upload="handleHeroUpload"
          />
        </LabelValue>
      </FeatureFencingWrapper>

      <LabelValue
        v-if="heroIsVideo"
        data-testid="product-hero-video-thumbnail-field"
        :label="t('Reviews.product.view.heroVideoThumbnail.label').toString()"
      >
        <MediaUpload
          data-testid="product-hero-video-thumbnail-upload"
          :accept="HERO_VIDEO_THUMBNAIL_MIME_TYPES.join(', ')"
          aspect-ratio="16:9"
          :disabled="heroImageFenced"
          max-width="648px"
          :preview-asset="heroImageFenced ? undefined : product.heroVideoThumbnail || undefined"
          :hint="t('Reviews.product.view.heroVideoThumbnail.hint').toString()"
          :short-hint="shortHeroVideoThumbnailHint"
          :error="getValidationErrors(v$.heroVideoThumbnail)"
          @upload="handleHeroVideoThumbnailUpload"
        />
      </LabelValue>

      <LabelValue
        :label="t('Reviews.product.view.description.label').toString()"
        :help-tooltip="t('Reviews.product.view.detailsEdit.productTextHelp')"
        :help-link="t('Reviews.product.view.detailsEdit.productTextHelpLink')"
      >
        <div class="flex gap-x-4">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex-1">
            <VuelidateField :validation="v$.shortDescription[lang]">
              <template #default="{ error, state, countHint }">
                <FieldWrapper :for="`short-description-${lang}`" :error="error" :count-hint="countHint">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.product.view.shortDescription.label') }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <TextareaField
                        :id="id"
                        v-model="v$.shortDescription[lang].$model"
                        :data-testid="`short-description-${lang}`"
                        :state="state"
                        :maxlength="SHORT_DESCRIPTION_MAX_LENGTH"
                        rows="3"
                        @keydown.enter.prevent
                      />
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
        </div>
        <div class="flex gap-x-4">
          <MarkdownInput
            v-for="lang in ALLOWED_LOCALES"
            :id="`description-${lang}`"
            :key="lang"
            v-model="v$.description[lang].$model"
            :max-length="props.product.hasPaidPlan ? undefined : DESCRIPTION_MAX_LENGTH"
            :error="getValidationErrors(v$.description[lang])"
            class="markdowneditor w-1/2"
            dense
            :allowed-tags="['h3', 'h4', 'h5', 'p']"
            :data-testid="`description-${lang}`"
          />
        </div>
      </LabelValue>

      <LabelValue
        :label="t('Reviews.product.view.links.label').toString()"
        show-icon
        :help-tooltip="!socialLinksFenced ? t('Reviews.product.view.detailsEdit.linksHelp') : undefined"
        :help-link="!socialLinksFenced ? t('Reviews.product.view.detailsEdit.linksHelpLink') : undefined"
      >
        <template #hint>
          {{ t('Reviews.product.edit.url.hint') }}
        </template>
        <div class="grid grid-cols-2 gap-x-6">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex items-center">
            <VuelidateField :validation="v$.url[lang]">
              <template #default="{ error, state }">
                <FieldWrapper :for="`website-url-${lang}`" :error="error" hint=" ">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.product.edit.url.label', { lang }) }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <InputField
                        :id="id"
                        v-model="v$.url[lang].$model"
                        :data-testid="`website-url-${lang}`"
                        type="text"
                        :state="state"
                        :placeholder="t('Reviews.product.edit.url.placeholder', { lang }).toString()"
                      >
                        <template #prepend>
                          <GlobeIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex items-center">
            <VuelidateField :validation="v$.linkText[lang]">
              <template #default="{ error, state }">
                <FieldWrapper :for="`website-link-text-${lang}`" :error="error" hint=" ">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.product.edit.web.label', { lang }) }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <InputField
                        :id="id"
                        v-model="v$.linkText[lang].$model"
                        :data-testid="`website-link-text-${lang}`"
                        type="text"
                        :state="state"
                        :placeholder="t('Reviews.product.edit.web.placeholder', { lang }).toString()"
                      >
                        <template #prepend>
                          <TypeIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
          <FeatureFencingWrapper
            :fenced="socialLinksFenced"
            :label="t('Reviews.product.view.detailsEdit.linksFencingLabel')"
            :help-tooltip="t('Reviews.product.view.detailsEdit.linksFencingTooltip')"
            :help-link="t('Reviews.product.view.detailsEdit.linksFencingHelpLink')"
            :upgrade-cta="t('Reviews.product.view.detailsEdit.linksFencingUpgradeCta')"
            :upgrade-cta-link="t('Reviews.product.view.detailsEdit.linksFencingUpgradeCtaLink')"
            placement="social_links"
            entity-type="tool"
            :entity-id="product.id"
            :class="{ 'col-span-2': socialLinksFenced }"
          >
            <div class="flex items-center" :class="{ 'w-1/2 pr-3 text-disabled': socialLinksFenced }">
              <VuelidateField :validation="v$.linkedin">
                <template #default="{ error, state }">
                  <FieldWrapper for="linkedin-link" label="LinkedIn" :error="error" hint=" ">
                    <template #default="{ id }">
                      <InputField
                        :id="id"
                        v-model="v$.linkedin.$model"
                        data-testid="linkedin-link"
                        type="text"
                        :state="state"
                        :disabled="socialLinksFenced"
                        :placeholder="t('Reviews.product.edit.linkedin.placeholder').toString()"
                      >
                        <template #prepend>
                          <LinkedinCustomIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </template>
                  </FieldWrapper>
                </template>
              </VuelidateField>
            </div>
            <div />
            <div class="flex items-center" :class="{ 'w-1/2 pr-3 text-disabled': socialLinksFenced }">
              <VuelidateField :validation="v$.twitter">
                <template #default="{ error, state }">
                  <FieldWrapper for="twitter-link" label="X" :error="error" hint=" ">
                    <template #default="{ id }">
                      <InputField
                        :id="id"
                        v-model="v$.twitter.$model"
                        data-testid="twitter-link"
                        type="text"
                        :state="state"
                        :disabled="socialLinksFenced"
                        :placeholder="t('Reviews.agency.edit.twitter.placeholder').toString()"
                      >
                        <template #prepend>
                          <XCustomIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </template>
                  </FieldWrapper>
                </template>
              </VuelidateField>
            </div>
          </FeatureFencingWrapper>
        </div>
      </LabelValue>

      <ReviewsCtaEditForm
        :data="product"
        :entity-id="product.id"
        :hint="t('Reviews.product.view.contact.primary.hint')"
        @change="handleCtaUpdate"
      />
    </div>
    <template #footer>
      <div class="my-6 flex flex-row items-center justify-end">
        <RouterLink
          class="btn-secondary-color-s mr-2"
          data-testid="cancel-button-footer"
          :to="{ name: ReviewsRouteName.ReviewsProductView, params: { productId: product.id } }"
          @click="track('profile_update_cancel', { surface: 'editor', entity_type: 'tool', entity_id: product.id })"
        >
          {{ t('Reviews.product.action.cancel') }}
        </RouterLink>
        <button class="btn-primary-color-s" data-testid="save-button-footer" @click="onSubmit">
          {{ t('Reviews.product.action.save') }}
        </button>
      </div>
    </template>
  </ContentCard>
</template>

<script setup lang="ts">
import { ChipBadge, FieldWrapper, InputField, TextareaField } from '@ramp106/omrjs-core-ui'
import { MarkdownInput } from '@ramp106/omrjs-core-ui/deprecated'
import { GlobeIcon, LinkedinCustomIcon, TypeIcon, XCustomIcon } from '@ramp106/omrjs-core-ui/icons'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import TurnDown from 'turndown'
import { computed, inject, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import detailsFencingImageUrl from '@/components/ReviewsCommon/Fencing/details_fencing.png'
import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import FencingBanner from '@/components/ReviewsCommon/Fencing/FencingBanner.vue'
import {
  LOGO_MAX_SIZE_MB,
  LOGO_MIME_TYPES,
  HERO_MAX_SIZE_MB,
  HERO_MIME_TYPES,
  HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB,
  HERO_VIDEO_THUMBNAIL_MIME_TYPES,
} from '@/components/ReviewsManagement/constants.ts'
import { type ProductAttributes, ProductConfigCtaPrimaryVendorEnum, type ProductData, type TranslationAttributes } from '@/gql/reviews'
import { useUpdateReviewsProductMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { getProductCategoryLabels } from '@/helpers/productCategoriesHelper'
import { ALLOWED_LOCALES, areTranslationsChanged } from '@/helpers/translationsHelper'
import {
  buildTranslationFieldsRules,
  containsEmailOrUrlRule,
  fileMaxSizeRule,
  fileMimeTypeRule,
  getValidationErrors,
  imageMaxDimensionRule,
  imageMinDimensionRule,
  urlRule,
  linkedinUrlRule,
} from '@/helpers/validationHelper'
import { ReviewsRouteName } from '@/router/types'
import { maxLength, maxLengthIf } from '@/services/validators'
import { reloadReviewsProductKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import LabelValue from '@/ui/LabelValue.vue'
import MediaUpload from '@/ui/MediaUpload.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

import ReviewsCtaEditForm from '../ReviewsCtaEditForm.vue'
import ReviewsProductTabs from '../ReviewsProductTabs.vue'
import type { CtaFormData, ReviewsProductEditFormData } from '../types'

const SHORT_DESCRIPTION_MAX_LENGTH = 180
const DESCRIPTION_MAX_LENGTH = 500

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const router = useRouter()

const productCategories = computed(() => getProductCategoryLabels(props.product, t('Reviews.product.view.category.main')))

const heroImageFenced = computed(() => {
  return !props.product.configFlags.heroEditable
})

const socialLinksFenced = computed(() => {
  return !props.product.configFlags.socialLinksAllowed
})

const featureMap = [
  { flag: !props.product.configFlags.heroEditable, key: 'Reviews.product.view.media.banner.headerImageFencing' },
  { flag: !props.product.configFlags.socialLinksAllowed, key: 'Reviews.product.view.media.banner.socialLinksFencing' },
  { flag: !props.product.configFlags.ctaPrimaryEditable, key: 'Reviews.product.view.media.banner.ctaPrimaryFencing' },
  { flag: !props.product.configFlags.ctaSecondaryEditable, key: 'Reviews.product.view.media.banner.ctaSecondaryFencing' },
]
const fencedFeatureList = computed(() => featureMap.filter((feature) => feature.flag).map((feature) => t(feature.key)))

const uploadProgress = ref(0)
const reloadReviewsProduct = inject(reloadReviewsProductKey) as () => void

const formData = reactive<ReviewsProductEditFormData>({
  description: { ...props.product.descriptionTranslationsMap },
  shortDescription: { ...props.product.shortDescriptionTranslationsMap },
  url: { ...props.product.urlTranslationsMap },
  linkText: { ...props.product.linkTextTranslationsMap },
  linkedin: props.product.linkedin || null,
  twitter: props.product.twitter || null,
  logo: null,
  hero: null,
  heroVideoThumbnail: null,
  ctaPrimaryEnabled: props.product.configFlags.ctaPrimaryActive,
  ctaSecondaryLabel: { ...props.product.ctaSecondaryLabelTranslationsMap },
  ctaSecondaryUrl: { ...props.product.ctaSecondaryUrlTranslationsMap },
})

const shortLogoHint = computed(() => {
  const acceptedMimeTypes = LOGO_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return [acceptedMimeTypes, `Max. ${LOGO_MIME_TYPES} MB`]
})

const heroHint = computed(() =>
  props.product.configFlags.heroActive
    ? t('Reviews.product.view.hero.hint').toString()
    : t('Reviews.product.view.hero.hintDisabled').toString(),
)

const shortHeroHint = computed(() => {
  const acceptedMimeTypes = HERO_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return props.product.configFlags.heroActive
    ? [acceptedMimeTypes, `Max. ${HERO_MAX_SIZE_MB} MB`]
    : t('Reviews.product.view.hero.shortHintDisabled').toString()
})

const heroIsVideo = computed(() => {
  if (formData.hero) {
    return formData.hero.type.startsWith('video')
  }

  return props.product.hero?.contentType?.startsWith('video') ?? false
})

const shortHeroVideoThumbnailHint = computed(() => {
  const acceptedMimeTypes = HERO_VIDEO_THUMBNAIL_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return [acceptedMimeTypes, `Max. ${HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB} MB`]
})

const rules = computed<ValidationArgs<ReviewsProductEditFormData>>(() => ({
  logo: {
    fileSize: fileMaxSizeRule(t, { megaBytes: LOGO_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: LOGO_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 64, minHeight: 64 }),
    maxDimension: imageMaxDimensionRule(t, { maxWidth: 1024, maxHeight: 1024 }),
  },
  hero: {
    fileSize: fileMaxSizeRule(t, { megaBytes: HERO_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: HERO_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 646, minHeight: 364 }),
  },
  heroVideoThumbnail: {
    fileSize: fileMaxSizeRule(t, { megaBytes: HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: HERO_VIDEO_THUMBNAIL_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 646, minHeight: 364 }),
  },
  description: buildTranslationFieldsRules({
    maxLength: maxLengthIf(DESCRIPTION_MAX_LENGTH, !props.product.hasPaidPlan),
    containsEmailOrUrl: containsEmailOrUrlRule(t, { isActive: !props.product.hasPaidPlan, allowedTitle: props.product.title }),
  }),
  shortDescription: buildTranslationFieldsRules({ maxLength: maxLength(SHORT_DESCRIPTION_MAX_LENGTH) }),
  linkText: buildTranslationFieldsRules({}),
  url: buildTranslationFieldsRules({ urlFormat: urlRule(t) }),
  linkedin: { urlFormat: linkedinUrlRule(t) },
  twitter: { urlFormat: urlRule(t) },
  ctaPrimaryEnabled: {},
  ctaSecondaryLabel: {},
  ctaSecondaryUrl: {},
}))

const v$ = useVuelidate(rules, formData)

function handleLogoUpload(file: File) {
  v$.value.logo.$model = file
}

function handleHeroUpload(file: File) {
  v$.value.hero.$model = file
}

function handleHeroVideoThumbnailUpload(file: File) {
  v$.value.heroVideoThumbnail.$model = file
}

watch(heroIsVideo, (isVideo) => {
  if (!isVideo) {
    v$.value.heroVideoThumbnail.$model = null
  }
})

const {
  mutate: mutateProduct,
  loading: loadingUpdateProduct,
  onDone,
  onError,
} = useUpdateReviewsProductMutation({
  clientId: 'reviews',
  context: {
    fetchOptions: {
      useUpload: true,
      onProgress: (progress: ProgressEvent) => {
        uploadProgress.value = Math.round((progress.loaded / progress.total) * 100)
      },
    },
  },
})

const loading = computed(() => loadingUpdateProduct.value)

const handleCtaUpdate = (data: CtaFormData) => {
  v$.value.ctaPrimaryEnabled.$model = data.ctaPrimaryEnabled
  v$.value.ctaSecondaryUrl.$model = { ...data.ctaSecondaryUrl }
  v$.value.ctaSecondaryLabel.$model = { ...data.ctaSecondaryLabel }
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id })
  v$.value.$touch()
  if (v$.value.$invalid) {
    showNotification(t('Reviews.product.notifications.invalidData'), 'error')
    return
  }

  try {
    const uploadedHeroImage = formData.hero && formData.hero.type.startsWith('image')
    const uploadedHeroVideo = formData.hero && formData.hero.type.startsWith('video')

    let heroImage
    let heroVideo
    if (uploadedHeroImage) {
      heroImage = formData.hero
      heroVideo = null
    }
    if (uploadedHeroVideo) {
      heroVideo = formData.hero
      heroImage = null
    }

    const turnDown = new TurnDown({
      emDelimiter: '_',
      linkStyle: 'inlined',
      headingStyle: 'atx',
    })

    const descriptionTranslations: TranslationAttributes[] = []
    const shortDescriptionTranslations: TranslationAttributes[] = []
    const linkTextTranslations: TranslationAttributes[] = []
    const urlTranslations: TranslationAttributes[] = []

    ALLOWED_LOCALES.forEach((locale) => {
      if (
        v$.value.description[locale]?.$dirty &&
        v$.value.description[locale].$model !== props.product.descriptionTranslationsMap[locale]
      ) {
        descriptionTranslations.push({
          locale,
          value: formData.description[locale] ? turnDown.turndown(formData.description[locale]) : '',
        })
      }

      shortDescriptionTranslations.push({
        locale,
        value: formData.shortDescription[locale],
      })

      linkTextTranslations.push({
        locale,
        value: formData.linkText[locale],
      })

      urlTranslations.push({
        locale,
        value: formData.url[locale],
      })
    })

    const attributes: ProductAttributes = {
      logo: formData.logo ? formData.logo : undefined,
      heroImage,
      heroVideo,
      heroVideoThumbnail: formData.heroVideoThumbnail ? formData.heroVideoThumbnail : undefined,
      descriptionTranslations,
      shortDescriptionTranslations,
      urlTranslations,
      linkTextTranslations,
      configAttributes: {},
    }

    if (v$.value.ctaPrimaryEnabled.$dirty) {
      attributes.configAttributes = {
        ...attributes.configAttributes,
        ctaPrimaryVendor: formData.ctaPrimaryEnabled ? ProductConfigCtaPrimaryVendorEnum.Automatic : ProductConfigCtaPrimaryVendorEnum.No,
      }
    }

    if (props.product.configFlags.socialLinksAllowed) {
      if (formData.linkedin !== (props.product.linkedin || null)) {
        attributes.linkedin = formData.linkedin
      }
      if (formData.twitter !== (props.product.twitter || null)) {
        attributes.twitter = formData.twitter
      }
    }

    if (
      props.product.configFlags.ctaSecondaryEditable &&
      (areTranslationsChanged(formData.ctaSecondaryLabel, props.product.ctaSecondaryLabelTranslationsMap) ||
        areTranslationsChanged(formData.ctaSecondaryUrl, props.product.ctaSecondaryUrlTranslationsMap))
    ) {
      const ctaSecondaryLabelTranslations: TranslationAttributes[] = []
      const ctaSecondaryUrlTranslations: TranslationAttributes[] = []
      ALLOWED_LOCALES.forEach((locale) => {
        ctaSecondaryLabelTranslations.push({
          locale,
          value: formData.ctaSecondaryLabel[locale],
        })
        ctaSecondaryUrlTranslations.push({
          locale,
          value: formData.ctaSecondaryUrl[locale],
        })
      })
      attributes.ctaSecondaryLabelTranslations = ctaSecondaryLabelTranslations
      attributes.ctaSecondaryUrlTranslations = ctaSecondaryUrlTranslations
    }

    await mutateProduct({
      id: props.product.id,
      attributes,
    })
  } catch {
    uploadProgress.value = 0
  }
}

onDone(() => {
  uploadProgress.value = 0
  reloadReviewsProduct()
  showNotification(t('Reviews.product.notifications.savedSuccessfully', { name: props.product.title }), 'success')
  router.push({ name: ReviewsRouteName.ReviewsProductView, params: { productId: props.product.id } })
})

onError((error) => showError(error))

const showError = (error: Error) => {
  uploadProgress.value = 0
  if (error) showNotification(error.message, 'error')
}
</script>
<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';
.text-disabled :deep(label) {
  @apply text-disabled;
}

:deep(.markdowneditor) {
  ul {
    list-style: disc;
    padding: 0 1.3em;
  }
  ol {
    list-style: decimal;
    padding: 0 1.3em;
  }
  h3 {
    @apply text-headline-m;
  }
  h4 {
    @apply text-headline-s;
  }
  h5 {
    @apply text-headline-xs;
  }
}
</style>
